import React, { useEffect, useMemo, useRef, useState } from 'react';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import { finalize } from 'rxjs/operators';
import { isPackageAllowed, getStatus } from 'utils/packages/packageState';
import { translate } from 'components/Translation';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { SinglePackage } from 'utils/api/models/numbers/singlePackage';
import AccordionContent from './common/AccordionContent';
import { SingleSubscriberProduct } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import apiConfig from 'utils/api/config';
import { SERVICE_STATUS } from '../constants';

interface IFixProps {
	subscriberProducts: SingleSubscriberProduct[];
	showOnlyActive: boolean;
	updateOrderItems: types.services.updateOrderItemsFunction;
}

const Fix = ({ subscriberProducts, showOnlyActive, updateOrderItems }: IFixProps) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [packages, setPackages] = useState<SinglePackage[]>([]);
	const isMounted = useRef(true);
	const dispatch = useDispatch();

	const updatePackage = (singlePackage: types.services.IPackage) => {
		const action = singlePackage.status?.type === 'ACTIVE' ? 'TURN_OFF' : 'TURN_ON';
		const orderItemAction: 'REMOVE' | 'ADD' = singlePackage.status?.type === 'ACTIVE' ? 'REMOVE' : 'ADD';
		const serviceName = singlePackage.name;
		const orderItems = [
			{
				id: singlePackage.legacyProductId,
				action: orderItemAction,
				place: singlePackage.place,
			},
		];
		updateOrderItems(orderItems, action, serviceName);
	};

	useEffect(() => {
		setLoading(true);
		dispatch(showLoadingIndicator());
		ECommerceService.getPackages('ALL', 'FIX')
			.pipe(
				finalize(() => {
					if (isMounted.current) {
						setLoading(false);
					}
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe(({ packages }) => {
				if (isMounted.current) {
					setPackages(
						packages.filter((singlePackage: SinglePackage) =>
							isPackageAllowed(subscriberProducts, singlePackage)
						)
					);
				}
			});
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [subscriberProducts, dispatch]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const accordionPackages: types.services.IPackage[] = useMemo(() => {
		return packages.map((singlePackage) => {
			const getPackageStatus = getStatus(subscriberProducts, singlePackage);
			return {
				name: singlePackage.name,
				id: singlePackage.partNumber,
				status: {
					type: getPackageStatus,
				},
				legacyProductId: singlePackage.partNumber,
				place: singlePackage.place,
				ebuConfigProduct: singlePackage.ebuConfigProduct,
				description: translate(
					'services.fix.description.fee',
					singlePackage.installationFee || '-',
					singlePackage.monthlyFee || '-'
				),
				buttons:
					singlePackage.selfActivate &&
					(getPackageStatus === SERVICE_STATUS.ACTIVE || getPackageStatus === SERVICE_STATUS.INACTIVE)
						? [
								{
									text:
										getPackageStatus === SERVICE_STATUS.ACTIVE
											? translate('numbers.services.turn.off')
											: translate('numbers.services.turn.on'),
									color:
										getPackageStatus === SERVICE_STATUS.ACTIVE ? 'tertiary-light' : 'primary-light',
									type: getPackageStatus === SERVICE_STATUS.ACTIVE ? 'TURN_OFF' : 'TURN_ON',
								},
						  ]
						: [],
			};
		});
	}, [subscriberProducts, packages]);

	const filteredAccordionPackages = useMemo(() => {
		if (showOnlyActive) {
			return accordionPackages.filter((singlePackage) => singlePackage.status?.type === 'ACTIVE');
		}
		return accordionPackages;
	}, [accordionPackages, showOnlyActive]);

	return (
		<ConditionalRender
			show={loading}
			onTrue={() => <CustomCircularProgress text={translate('number.loading.packages')} loading={loading} />}
			onFalse={() => (
				<AccordionContent
					elements={filteredAccordionPackages}
					termsAndConditions={{
						type: 'link',
						url: apiConfig.links.enterpriseGovernment,
					}}
					updateService={updatePackage}
				/>
			)}
		/>
	);
};

export default Fix;
